import { UserManager, WebStorageStateStore } from 'oidc-client-ts';

type AuthState = {
  returnTo?: string;
};

let userManager: UserManager | null = null;
let loginRedirectInFlight = false;

const getRequiredEnv = (name: keyof ImportMetaEnv): string => {
  const value = import.meta.env[name];
  if (!value) {
    throw new Error(`Missing required auth environment variable: ${name}`);
  }
  return value;
};

const getUserManager = (): UserManager => {
  if (userManager) {
    return userManager;
  }

  const authority = getRequiredEnv('VITE_OIDC_AUTHORITY');
  const clientId = getRequiredEnv('VITE_OIDC_CLIENT_ID');
  const redirectUri =
    import.meta.env.VITE_OIDC_REDIRECT_URI || `${window.location.origin}/auth/callback`;
  const postLogoutRedirectUri =
    import.meta.env.VITE_OIDC_POST_LOGOUT_REDIRECT_URI ||
    `${window.location.origin}/auth/logout-callback`;
  const scope = import.meta.env.VITE_OIDC_SCOPE || 'openid profile email';

  userManager = new UserManager({
    authority,
    client_id: clientId,
    redirect_uri: redirectUri,
    post_logout_redirect_uri: postLogoutRedirectUri,
    response_type: 'code',
    scope,
    automaticSilentRenew: false,
    userStore: new WebStorageStateStore({ store: window.localStorage }),
  });

  return userManager;
};

export const getAccessToken = async (): Promise<string | null> => {
  const user = await getUserManager().getUser();
  if (!user || user.expired) {
    return null;
  }
  return user.access_token || null;
};

export const isAuthenticated = async (): Promise<boolean> => {
  const token = await getAccessToken();
  return Boolean(token);
};

export const beginLoginRedirect = async (returnTo?: string): Promise<void> => {
  if (loginRedirectInFlight) {
    return;
  }
  loginRedirectInFlight = true;
  const target = returnTo || `${window.location.pathname}${window.location.search}`;
  await getUserManager().signinRedirect({ state: { returnTo: target } satisfies AuthState });
};

export const handleLoginCallback = async (): Promise<string> => {
  const user = await getUserManager().signinRedirectCallback();
  const state = (user.state || {}) as AuthState;
  return state.returnTo || '/';
};

export const beginLogoutRedirect = async (): Promise<void> => {
  await getUserManager().signoutRedirect();
};

export const handleLogoutCallback = async (): Promise<void> => {
  await getUserManager().signoutRedirectCallback();
};

export const handleUnauthorized = async (): Promise<void> => {
  await getUserManager().removeUser();
  await beginLoginRedirect();
};

export const getAuthenticatedUserEmail = async (): Promise<string | null> => {
  const user = await getUserManager().getUser();
  if (!user || user.expired) {
    return null;
  }

  const emailClaim = user.profile.email;
  return typeof emailClaim === 'string' ? emailClaim : null;
};
